import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const STEPS = [
  {
    icon: 'ri-book-open-line',
    title: 'Ações de Formação',
    desc: 'Cria, edita e gere todas as ações de formação da Câmara. Define datas, entidades formadoras e orçamentos.',
    color: '#16a34a',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Participantes',
    desc: 'Regista quem frequentou cada formação, avalia participações e emite certificados de formação.',
    color: '#16a34a',
  },
  {
    icon: 'ri-team-line',
    title: 'Funcionários',
    desc: 'Consulta o histórico formativo de cada funcionário, identifica lacunas e planeia formações futuras.',
    color: '#16a34a',
  },
  {
    icon: 'ri-clipboard-line',
    title: 'Plano de Formação',
    desc: 'Elabora o plano anual de formação, define objetivos e acompanha a execução ao longo do ano.',
    color: '#16a34a',
  },
  {
    icon: 'ri-calendar-event-line',
    title: 'Calendário',
    desc: 'Visualiza todas as formações num calendário interativo. Exporta e partilha datas com as equipas.',
    color: '#16a34a',
  },
  {
    icon: 'ri-pie-chart-2-line',
    title: 'Relatórios',
    desc: 'Gera relatórios de formação, balanço social e estatísticas para apresentar aos órgãos competentes.',
    color: '#16a34a',
  },
];

const ONBOARDING_KEY = 'formacao_onboarding_seen';

export default function FormacaoOnboardingPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimated(true), 100);
  }, []);

  const handleFinish = () => {
    try {
      localStorage.setItem(ONBOARDING_KEY, 'true');
      localStorage.setItem(`${ONBOARDING_KEY}_date`, new Date().toISOString());
    } catch {
      // ignore
    }
    navigate('/formacao');
  };

  const handleSkip = () => {
    handleFinish();
  };

  const initials = profile?.full_name
    ? profile.full_name
        .split(' ')
        .filter((_: string, i: number, arr: string[]) => i === 0 || i === arr.length - 1)
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
    : '?';

  return (
    <div className="min-h-screen bg-[#f5f5f3] flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-zinc-100 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
            <i className="ri-book-open-line text-emerald-600 text-sm" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-zinc-800">Gestão da Formação</p>
            <p className="text-[11px] text-zinc-400">Câmara Municipal</p>
          </div>
        </div>
        <button
          onClick={handleSkip}
          className="text-[12px] text-zinc-400 hover:text-zinc-600 font-medium cursor-pointer transition-colors"
        >
          Saltar <i className="ri-arrow-right-line" />
        </button>
      </div>

      {/* Hero */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 md:py-16">
        <div className="max-w-3xl w-full">
          {/* Welcome */}
          <div
            className={`text-center mb-10 transition-all duration-700 ${
              animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-emerald-100 mx-auto mb-5">
              <span className="text-[20px] font-bold text-emerald-600">{initials}</span>
            </div>
            <h1 className="text-[22px] md:text-[28px] font-bold text-zinc-900 tracking-tight mb-2">
              Bem-vindo{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-[13px] md:text-[14px] text-zinc-400 max-w-lg mx-auto leading-relaxed">
              Este é o módulo de Gestão da Formação da Câmara Municipal. Aqui podes gerir todas as ações de formação,
              participantes, funcionários, calendário e muito mais.
            </p>
          </div>

          {/* Progress steps */}
          <div
            className={`flex items-center justify-center gap-2 mb-8 transition-all duration-700 delay-100 ${
              animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  i <= currentStep ? 'bg-emerald-500 w-8' : 'bg-zinc-200 w-4'
                }`}
              />
            ))}
          </div>

          {/* Feature cards grid */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 transition-all duration-700 delay-200 ${
              animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {STEPS.map((step, i) => {
              const isActive = i === currentStep;
              return (
                <button
                  key={i}
                  onClick={() => setCurrentStep(i)}
                  className={`text-left p-5 rounded-2xl border transition-all duration-300 cursor-pointer group ${
                    isActive
                      ? 'bg-white border-emerald-200 ring-1 ring-emerald-100'
                      : 'bg-white border-zinc-100 hover:border-zinc-200'
                  }`}
                >
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 transition-colors ${
                      isActive ? 'bg-emerald-100' : 'bg-zinc-100 group-hover:bg-zinc-50'
                    }`}
                  >
                    <i
                      className={`${step.icon} text-sm transition-colors ${
                        isActive ? 'text-emerald-600' : 'text-zinc-400'
                      }`}
                    />
                  </div>
                  <h3
                    className={`text-[13px] font-semibold mb-1 ${
                      isActive ? 'text-emerald-700' : 'text-zinc-700'
                    }`}
                  >
                    {step.title}
                  </h3>
                  <p className="text-[12px] text-zinc-400 leading-relaxed">{step.desc}</p>
                  {isActive && (
                    <div className="mt-3 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
                      <i className="ri-check-line" /> A explorar
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Active step detail */}
          <div
            className={`bg-white rounded-2xl border border-zinc-100 p-6 mb-8 transition-all duration-500 ${
              animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-100 flex-shrink-0">
                <i className={`${STEPS[currentStep].icon} text-emerald-600 text-lg`} />
              </div>
              <div className="flex-1">
                <h3 className="text-[15px] font-semibold text-zinc-800 mb-1">
                  {STEPS[currentStep].title}
                </h3>
                <p className="text-[13px] text-zinc-500 leading-relaxed mb-4">
                  {STEPS[currentStep].desc}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
                    disabled={currentStep === 0}
                    className={`px-4 py-2 text-[12px] font-semibold rounded-lg border transition-all cursor-pointer ${
                      currentStep === 0
                        ? 'bg-zinc-50 text-zinc-300 border-zinc-100 cursor-not-allowed'
                        : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                    }`}
                  >
                    <i className="ri-arrow-left-line" /> Anterior
                  </button>
                  {currentStep < STEPS.length - 1 ? (
                    <button
                      onClick={() => setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1))}
                      className="px-4 py-2 bg-zinc-900 text-white text-[12px] font-semibold rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Próximo <i className="ri-arrow-right-line" />
                    </button>
                  ) : (
                    <button
                      onClick={handleFinish}
                      className="px-5 py-2 bg-emerald-600 text-white text-[12px] font-semibold rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-rocket-line" /> Começar a usar
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions footer */}
          <div
            className={`text-center transition-all duration-700 delay-300 ${
              animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <p className="text-[12px] text-zinc-400 mb-3">Ou acede diretamente a:</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {[
                { to: '/formacao', label: 'Dashboard', icon: 'ri-dashboard-line' },
                { to: '/formacao/acoes', label: 'Ações', icon: 'ri-book-open-line' },
                { to: '/formacao/funcionarios', label: 'Funcionários', icon: 'ri-team-line' },
                { to: '/formacao/calendario', label: 'Calendário', icon: 'ri-calendar-event-line' },
              ].map((link) => (
                <a
                  key={link.to}
                  href={link.to}
                  className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-zinc-600 bg-white border border-zinc-200 rounded-xl hover:border-zinc-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${link.icon} text-xs`} /> {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}